import React, { useState, useEffect } from "react";
import { Plus, Edit2, Trash2, Search, FileText, Calendar, Building, CheckCircle, XCircle } from "lucide-react";
import api from "../../services/api";

export const ContractManagement = () => {
    const [contracts, setContracts] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingContract, setEditingContract] = useState(null);
    const [formData, setFormData] = useState({
        company_name: "",
        cnpj: "",
        service_type: "",
        contract_number: "",
        start_date: "",
        end_date: "",
        status: "Ativo",
        description: ""
    });

    const fetchContracts = async () => {
        setIsLoading(true);
        try {
            const response = await api.get("/contracts");
            setContracts(response.data);
        } catch (error) {
            console.error("Erro ao carregar contratos:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchContracts();
    }, []);

    const handleDelete = async (id) => {
        if (window.confirm("Tem certeza que deseja excluir este contrato?")) {
            try {
                await api.delete(`/contracts/${id}`);
                fetchContracts();
            } catch (error) {
                alert("Erro ao excluir contrato");
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingContract) {
                await api.patch(`/contracts/${editingContract.id}`, formData);
            } else {
                await api.post("/contracts", formData);
            }
            setIsModalOpen(false);
            setEditingContract(null);
            resetForm();
            fetchContracts();
        } catch (error) {
            console.error("Erro ao salvar contrato:", error);
            alert("Erro ao salvar contrato. Verifique os dados.");
        }
    };

    const resetForm = () => {
        setFormData({
            company_name: "",
            cnpj: "",
            service_type: "",
            contract_number: "",
            start_date: "",
            end_date: "",
            status: "Ativo",
            description: ""
        });
    };

    const openEditModal = (contract) => {
        setEditingContract(contract);
        setFormData({
            company_name: contract.company_name,
            cnpj: contract.cnpj || "",
            service_type: contract.service_type,
            contract_number: contract.contract_number || "",
            start_date: contract.start_date,
            end_date: contract.end_date,
            status: contract.status,
            description: contract.description || ""
        });
        setIsModalOpen(true);
    };

    const filteredContracts = contracts.filter(c =>
        c.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.service_type.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getStatusBadge = (status) => {
        const styles = {
            'Ativo': 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800',
            'Vencido': 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800',
            'Cancelado': 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700',
            'Pendente': 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-800'
        };
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || styles['Ativo']}`}>
                {status}
            </span>
        );
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-100 dark:border-gray-800">
                <div>
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-white flex items-center gap-2">
                        <FileText className="w-5 h-5 text-blue-600" />
                        Gerenciamento de Contratos
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Controle empresas terceirizadas e vigência de serviços.
                    </p>
                </div>
                <button
                    onClick={() => {
                        setEditingContract(null);
                        resetForm();
                        setIsModalOpen(true);
                    }}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
                >
                    <Plus size={18} />
                    Novo Contrato
                </button>
            </div>

            {/* Search */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                    type="text"
                    placeholder="Buscar por empresa ou serviço..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                />
            </div>

            {/* Table */}
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-800">
                            <tr>
                                <th className="p-4 font-medium text-gray-500 dark:text-gray-400">Empresa</th>
                                <th className="p-4 font-medium text-gray-500 dark:text-gray-400">Serviço</th>
                                <th className="p-4 font-medium text-gray-500 dark:text-gray-400">Vigência</th>
                                <th className="p-4 font-medium text-gray-500 dark:text-gray-400">Status</th>
                                <th className="p-4 font-medium text-gray-500 dark:text-gray-400 text-right">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                            {isLoading ? (
                                <tr>
                                    <td colSpan="5" className="p-8 text-center text-gray-500">
                                        Carregando contratos...
                                    </td>
                                </tr>
                            ) : filteredContracts.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="p-8 text-center text-gray-500">
                                        Nenhum contrato encontrado.
                                    </td>
                                </tr>
                            ) : (
                                filteredContracts.map((contract) => (
                                    <tr key={contract.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors">
                                        <td className="p-4">
                                            <div className="font-medium text-gray-800 dark:text-white">
                                                {contract.company_name}
                                            </div>
                                            <div className="text-sm text-gray-500">
                                                CNPJ: {contract.cnpj || "N/A"}
                                            </div>
                                        </td>
                                        <td className="p-4 text-gray-600 dark:text-gray-300">
                                            {contract.service_type}
                                            <div className="text-xs text-gray-400">
                                                Nº: {contract.contract_number || "S/N"}
                                            </div>
                                        </td>
                                        <td className="p-4">
                                            <div className="flex flex-col gap-1 text-sm">
                                                <span className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                                                    <span className="text-xs font-semibold w-8">INÍCIO:</span>
                                                    {new Date(contract.start_date).toLocaleDateString()}
                                                </span>
                                                <span className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                                                    <span className="text-xs font-semibold w-8">FIM:</span>
                                                    {new Date(contract.end_date).toLocaleDateString()}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="p-4">
                                            {getStatusBadge(contract.status)}
                                        </td>
                                        <td className="p-4 text-right">
                                            <div className="flex justify-end gap-2">
                                                <button
                                                    onClick={() => openEditModal(contract)}
                                                    className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                                                    title="Editar"
                                                >
                                                    <Edit2 size={16} />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(contract.id)}
                                                    className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                                                    title="Excluir"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden border border-gray-200 dark:border-gray-800">
                        <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center bg-gray-50 dark:bg-gray-800/50">
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                                {editingContract ? "Editar Contrato" : "Novo Contrato"}
                            </h3>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                            >
                                <XCircle size={24} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Nome da Empresa *
                                    </label>
                                    <div className="relative">
                                        <Building className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                                        <input
                                            type="text"
                                            required
                                            value={formData.company_name}
                                            onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                                            className="w-full pl-9 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 outline-none"
                                            placeholder="Ex: CleanMax Serviços"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-1">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        CNPJ
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.cnpj}
                                        onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
                                        className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 outline-none"
                                        placeholder="00.000.000/0000-00"
                                    />
                                </div>

                                <div className="space-y-1">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Tipo de Serviço *
                                    </label>
                                    <select
                                        required
                                        value={formData.service_type}
                                        onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                                        className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 outline-none"
                                    >
                                        <option value="">Selecione...</option>
                                        <option value="Limpeza">Limpeza</option>
                                        <option value="Manutenção">Manutenção</option>
                                        <option value="Segurança">Segurança</option>
                                        <option value="Serviços Gerais">Serviços Gerais</option>
                                        <option value="Outros">Outros</option>
                                    </select>
                                </div>

                                <div className="space-y-1">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Status
                                    </label>
                                    <select
                                        value={formData.status}
                                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                        className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 outline-none"
                                    >
                                        <option value="Ativo">Ativo</option>
                                        <option value="Vencido">Vencido</option>
                                        <option value="Pendente">Pendente</option>
                                        <option value="Cancelado">Cancelado</option>
                                    </select>
                                </div>

                                <div className="space-y-1">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Data de Início *
                                    </label>
                                    <div className="relative">
                                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                                        <input
                                            type="date"
                                            required
                                            value={formData.start_date}
                                            onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                                            className="w-full pl-9 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 outline-none"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-1">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Data de Fim *
                                    </label>
                                    <div className="relative">
                                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                                        <input
                                            type="date"
                                            required
                                            value={formData.end_date}
                                            onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                                            className="w-full pl-9 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 outline-none"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-1">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Número do Contrato
                                </label>
                                <input
                                    type="text"
                                    value={formData.contract_number}
                                    onChange={(e) => setFormData({ ...formData, contract_number: e.target.value })}
                                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 outline-none"
                                    placeholder="Ex: CTR-2024/005"
                                />
                            </div>

                            <div className="space-y-1">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Descrição / Observações
                                </label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    rows="3"
                                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                                    placeholder="Detalhes adicionais sobre o contrato..."
                                ></textarea>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-800">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                                >
                                    <CheckCircle size={18} />
                                    {editingContract ? "Salvar Alterações" : "Criar Contrato"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};
